import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useParams } from "react-router-dom";
import {
  putEmployeeDocuments,
  postEmployeeDocuments,
  getEmployeeDocuments,
  deleteEmployeeDocuments,
  employeeDocumentApprove,
} from "app/services/Services";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import { baseURL } from "app/Api/axios";
import ConfirmModal from "./ConfirmModal";
import {
  T,
  sectionCardSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
} from "./shared/formKit";

const documentTypes = [
  "Resume/CV",
  "ID Proof",
  "Address Proof",
  "Educational Certificates",
  "Experience Letters",
  "Bank Statements",
];

// Single source of truth for which document types are mandatory -- mirrors
// the backend's own required-docs list (employee-document-controller.ts,
// addEmployeeDocuments) so the * shown here never drifts from what the API
// actually enforces.
const requiredDocumentTypes = ["ID Proof", "Experience Letters", "Educational Certificates", "Bank Statements"];

const DocumentsTab = ({ mode = "add", initialDocuments = [], onCompleted }) => {
  const [documents, setDocuments] = useState(initialDocuments.length > 0 ? initialDocuments : []);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: "", description: "", onConfirm: null });
  const { id } = useParams();

  const role = JSON.parse(localStorage.getItem("role"));

  useEffect(() => {
    fetchExistingDocuments();
  }, [mode, id]);

  const fetchExistingDocuments = async () => {
    try {
      setLoading(true);
      const response = await getEmployeeDocuments(id);

      if (response.data && response.data.status === "success") {
        setExistingDocuments(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
      generateErrorMessage("Failed to fetch existing documents");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      return "Only PDF and image files (JPEG, PNG, GIF, WebP) are allowed";
    }
    if (file.size > maxSize) {
      return "File size must be less than 5 MB";
    }
    return null;
  };

  const handleUpload = (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      generateErrorMessage(validationError);
      e.target.value = "";
      return;
    }

    const updated = [...documents];
    const existingIndex = updated.findIndex((doc) => doc.type === docType);

    const documentData = {
      type: docType,
      file: file,
      employeeId: id || null,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      isNew: true,
    };

    if (existingIndex !== -1) {
      updated[existingIndex] = documentData;
    } else {
      updated.push(documentData);
    }

    setDocuments(updated);
  };

  const handleRemove = (docType) => {
    const updated = documents.filter((doc) => doc.type !== docType);
    setDocuments(updated);
  };

  const handleDeleteClick = (documentId, docType) => {
    setModalConfig({
      title: "Delete Document?",
      description: `Are you sure you want to delete this document (${docType})? This action cannot be undone.`,
      onConfirm: () => confirmDeleteExisting(documentId, docType),
    });
    setConfirmModalOpen(true);
  };

  const confirmDeleteExisting = async (documentId) => {
    try {
      setLoading(true);
      const response = await deleteEmployeeDocuments(documentId);

      if (response.data && response.data.status === "success") {
        generateSuccessMessage("Document deleted successfully");
        setExistingDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
        setConfirmModalOpen(false);
      } else {
        throw new Error(response.data?.message || "Failed to delete document");
      }
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || err.message || "Failed to delete document");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRejectClick = (documentId, isApprove) => {
    const action = isApprove ? "approve" : "reject";
    setModalConfig({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Document?`,
      description: `Are you sure you want to ${action} this document?`,
      onConfirm: () => confirmApproveReject(documentId, isApprove),
    });
    setConfirmModalOpen(true);
  };

  const confirmApproveReject = async (documentId, isApprove) => {
    const action = isApprove ? "approve" : "reject";

    try {
      setLoading(true);
      const response = await employeeDocumentApprove(documentId, { isApprove });

      if (response.data && response.data.status === "success") {
        generateSuccessMessage(`Document ${action}d successfully`);
        await fetchExistingDocuments();
        setConfirmModalOpen(false);
      } else {
        throw new Error(response.data?.message || `Failed to ${action} document`);
      }
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || err.message || `Failed to ${action} document`);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (file, documentPath = null) => {
    let url;
    if (file instanceof File) {
      url = URL.createObjectURL(file);
    } else {
      url = `${baseURL}/${documentPath}`;
    }
    window.open(url, "_blank");
  };

  const validateSubmission = () => {
    if (documents.length === 0) {
      generateErrorMessage("Please upload at least one document");
      return false;
    }

    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = documents.filter((doc) => doc.file.size > maxSize);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((doc) => doc.file.name).join(", ");
      generateErrorMessage(`File(s) too large: ${fileNames}. Max size allowed is 5 MB.`);
      return false;
    }

    const uploadedTypes = documents.map((doc) => doc.type);
    const existingTypes = existingDocuments.map((doc) => doc.document_type);
    const allAvailableTypes = [...new Set([...uploadedTypes, ...existingTypes])];
    const missing = requiredDocumentTypes.filter((req) => !allAvailableTypes.includes(req));

    if (missing.length > 0) {
      generateErrorMessage(`Required documents missing: ${missing.join(", ")}`);
      return false;
    }

    return true;
  };

  // Uploaded one document per request (instead of bundling every selected
  // file into a single multipart POST) so the request body always stays
  // under ~5MB -- bundling all slots together could push the combined
  // payload past the server/proxy body-size limit even though each file
  // individually passes the 5MB check, causing a 413.
  const handleSubmit = async () => {
    if (!validateSubmission()) return;

    const failed = [];

    try {
      setLoading(true);

      for (const doc of documents) {
        const formData = new FormData();
        formData.append("metadata", JSON.stringify([{ employeeId: id, documentType: doc.type }]));
        formData.append("files", doc.file);

        try {
          const response =
            mode === "edit"
              ? await putEmployeeDocuments(formData, id)
              : await postEmployeeDocuments(formData);

          if (!response.data || response.data.status !== "success") {
            throw new Error(response.data?.message || `Failed to upload ${doc.type}`);
          }
        } catch (err) {
          failed.push({ type: doc.type, message: err.response?.data?.message || err.message });
        }
      }

      if (failed.length > 0) {
        setDocuments((prev) => prev.filter((doc) => failed.some((f) => f.type === doc.type)));
        generateErrorMessage(
          `Failed to upload: ${failed.map((f) => `${f.type} (${f.message})`).join(", ")}`
        );
      } else {
        generateSuccessMessage("Documents uploaded successfully");
        setDocuments([]);
        if (onCompleted) onCompleted(documents);
      }

      await fetchExistingDocuments();
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || err.message || "Failed to upload documents");
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (docType) => {
    const newDoc = documents.find((doc) => doc.type === docType);
    const existingDoc = existingDocuments.find((doc) => doc.document_type === docType);

    if (newDoc) return <StatusChip label="Ready to upload" tone="warning" />;
    if (existingDoc) {
      if (existingDoc.is_approve === true) return <StatusChip label="Approved" tone="success" />;
      return <StatusChip label="Pending approval" tone="warning" />;
    }
    return <StatusChip label="Not uploaded" tone="neutral" />;
  };

  if (initialLoading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label="Loading documents…" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={sectionCardSx}>
        <SectionHeader title="Documents" sub="PDF or image files, max 5MB each. Fields marked * are required." />

        <Grid container spacing={2}>
          {documentTypes.map((docType, index) => {
            const uploaded = documents.find((doc) => doc.type === docType);
            const existingDoc = existingDocuments.find((doc) => doc.document_type === docType);

            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    border: `1px solid ${T.cardBorder}`,
                    borderRadius: T.inputR,
                    padding: "18px 16px",
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ color: T.muted, mb: "10px" }}>
                    <CloudUploadIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Box sx={{ fontFamily: T.body_f, fontSize: "13.5px", fontWeight: 600, color: T.ink, mb: "8px" }}>
                    {docType}
                    {requiredDocumentTypes.includes(docType) && (
                      <Box component="span" sx={{ color: T.danger }}> *</Box>
                    )}
                  </Box>

                  {getStatusChip(docType)}

                  <Box mt={2}>
                    <SecondaryButton component="label" disabled={loading} sx={{ padding: "7px 14px", fontSize: "13px" }}>
                      <CloudUploadIcon sx={{ fontSize: 15, verticalAlign: "middle", mr: "6px" }} />
                      {uploaded || existingDoc ? "Replace" : "Upload"}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => handleUpload(e, docType)}
                        disabled={loading}
                      />
                    </SecondaryButton>
                  </Box>

                  {(uploaded || existingDoc) && (
                    <Box mt={1} display="flex" justifyContent="center" gap={1}>
                      <IconButton
                        size="small"
                        sx={{ color: T.magenta }}
                        onClick={() => {
                          if (uploaded) handlePreview(uploaded.file);
                          else if (existingDoc) handlePreview(null, existingDoc.document_path);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: T.danger }}
                        onClick={() => {
                          if (uploaded) handleRemove(docType);
                          else if (existingDoc) handleDeleteClick(existingDoc.id, docType);
                        }}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  {role?.roleName !== "EMPLOYEE" && existingDoc && !uploaded && (
                    <Box mt={1} display="flex" justifyContent="center" gap={1} flexWrap="wrap">
                      {existingDoc.is_approve !== true && (
                        <SecondaryButton
                          type="button"
                          onClick={() => handleApproveRejectClick(existingDoc.id, true)}
                          disabled={loading}
                          sx={{ padding: "6px 12px", fontSize: "12.5px", color: T.successText, borderColor: "#cceedb" }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: "4px" }} />
                          Approve
                        </SecondaryButton>
                      )}
                      {existingDoc.is_approve !== false && (
                        <SecondaryButton
                          type="button"
                          onClick={() => handleApproveRejectClick(existingDoc.id, false)}
                          disabled={loading}
                          sx={{ padding: "6px 12px", fontSize: "12.5px", color: T.dangerText, borderColor: "rgba(224,84,104,0.3)" }}
                        >
                          <CancelIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: "4px" }} />
                          Reject
                        </SecondaryButton>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Box mt={4} textAlign="right">
          <PrimaryButton type="button" onClick={handleSubmit} disabled={documents.length === 0 || loading}>
            {loading ? "Uploading…" : mode === "edit" ? "Update documents" : "Submit documents"}
          </PrimaryButton>
        </Box>
      </Box>

      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setModalConfig({ title: "", description: "", onConfirm: null }); }}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        description={modalConfig.description}
        confirmText={modalConfig.title.includes("Delete") ? "Delete" : "Confirm"}
        cancelText="Cancel"
        loading={loading}
      />
    </Box>
  );
};

export default DocumentsTab;
